import React, { useCallback, useEffect, useMemo, useState } from 'react';
import texasApi, {
  SecurityScoreResult,
  SecurityPillarScore,
} from '../../services/texasApi';
import { landlordApi } from '../../services/api';

/**
 * F-091 — Texas Security & Safety Score page (route /texas/security-score).
 *
 * Brief (m-20260930T193000Z): score 1-100, grade, violations, recommendations,
 * per-property selector, export action. Reuses the TexasScore.tsx /
 * SubchapterDChecklist.tsx visual language. Backend is DONE (F-227/228/229);
 * the texasApi.securityScore.* client already exists — use it, do not add one.
 *
 * Note: /texas/score (TexasScore) is a different working page and stays as is.
 */

const gradeFor = (pct: number): { grade: string; tone: string } => {
  if (pct >= 90) return { grade: 'A', tone: 'bg-emerald-100 text-emerald-700' };
  if (pct >= 80) return { grade: 'B', tone: 'bg-emerald-100 text-emerald-700' };
  if (pct >= 70) return { grade: 'C', tone: 'bg-amber-100 text-amber-700' };
  if (pct >= 60) return { grade: 'D', tone: 'bg-amber-100 text-amber-700' };
  return { grade: 'F', tone: 'bg-red-100 text-red-700' };
};

interface PropertyOption {
  id: string;
  name: string;
}

export const SecurityScorePage: React.FC = () => {
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [result, setResult] = useState<SecurityScoreResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await landlordApi.get('api/properties');
        const d = res?.data?.data ?? res?.data;
        const rows: any[] = Array.isArray(d) ? d : Array.isArray(d?.properties) ? d.properties : [];
        if (cancelled) return;
        const options = rows.map((p: any) => ({
          id: String(p.id ?? p.propertyId ?? p.propertyID ?? ''),
          name: String(p.name ?? p.propertyName ?? p.address ?? p.id ?? 'Property'),
        })).filter((p) => p.id);
        setProperties(options);
        if (options.length > 0) setSelectedId((prev) => prev || options[0].id);
      } catch {
        if (!cancelled) setLoadError('Failed to load properties');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadScore = useCallback(async (propertyId: string) => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await texasApi.securityScore.getProperty(propertyId);
      setResult(data);
    } catch {
      setError('Failed to load security score');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadScore(selectedId); }, [selectedId, loadScore]);

  const handleExport = async () => {
    if (!selectedId) return;
    setExporting(true);
    setError(null);
    try {
      const res = await texasApi.securityScore.exportProperty(selectedId);
      const payload: string = typeof res?.data === 'string'
        ? res.data
        : JSON.stringify(res?.data?.data ?? res?.data ?? {}, null, 2);
      const blob = new Blob([payload], { type: 'application/json' });
      // jsdom lacks blob URL methods; the tests stub createObjectURL. Guard so
      // an unexpected environment degrades to a no-op, never a crash.
      if (typeof URL.createObjectURL === 'function') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `security-score-${selectedId}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
      }
    } catch {
      setError('Failed to export security score');
    } finally {
      setExporting(false);
    }
  };

  const pct = useMemo(() => {
    if (!result || !result.overallMax) return 0;
    return Math.round((result.overallScore / result.overallMax) * 100);
  }, [result]);

  const scoreOutOf100 = useMemo(() => {
    if (!result || !result.overallMax) return 0;
    return Math.round((result.overallScore / result.overallMax) * 100);
  }, [result]);

  const { grade, tone } = gradeFor(pct);

  const violations = useMemo(() => {
    if (!result) return [] as SecurityPillarScore[];
    // failing = explicit passing:false, or no flag and not at max
    return (result.pillars || []).filter((p) => (p.passing ?? p.score >= p.maxScore) === false);
  }, [result]);

  const recommendations = useMemo(() => {
    if (!result) return [] as string[];
    const recs: string[] = [];
    (result.pillars || []).forEach((p) => {
      if ((p.passing ?? p.score >= p.maxScore) === false) {
        const detail = p.details && typeof p.details === 'object'
          ? (p.details as Record<string, unknown>).recommendation
          : undefined;
        recs.push(typeof detail === 'string' && detail.length > 0 ? detail : `Improve ${p.name} (${p.score}/${p.maxScore})`);
      }
    });
    return recs;
  }, [result]);

  return (
    <div data-testid="security-score-page" className="texas-page-container">
      <h1>Security &amp; Safety Score</h1>
      <p className="page-description">
        Per-property security and safety scoring across Texas properties (Ch. 92 Subchapter D / SB 38).
      </p>

      {loadError && <div className="error" data-testid="properties-error">{loadError}</div>}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="security-score-property" className="text-xs font-bold text-slate-500 tracking-wider">
              PROPERTY
            </label>
            <select
              id="security-score-property"
              data-testid="security-score-property-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
            >
              {properties.length === 0 && <option value="">No properties</option>}
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || !selectedId}
            data-testid="security-score-export-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            {exporting ? 'Exporting…' : 'Export report'}
          </button>
        </div>
      </section>

      {error && <div className="error" data-testid="error">{error}</div>}

      {loading ? (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="no-results">Loading score…</div>
        </section>
      ) : result ? (
        <>
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs font-bold text-slate-500 tracking-wider">SCORE</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="text-5xl font-black text-emerald-600" data-testid="security-score-value">{result.overallScore}</div>
                  <div className="text-sm text-slate-500">/ {result.overallMax}</div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tone}`} data-testid="security-score-grade">
                    {grade}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-500 tracking-wider">STATUS</div>
                <div className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${result.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {result.passed ? 'PASSING' : 'NOT PASSING'}
                </div>
                {result.evaluatedAt && (
                  <div className="text-xs text-slate-400 mt-1">Evaluated {result.evaluatedAt}</div>
                )}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4" data-testid="security-score-pillars">
            <h2>Pillar breakdown</h2>
            <div className="space-y-2">
              {(result.pillars || []).map((p) => {
                const pillarPct = p.maxScore > 0 ? Math.round((p.score / p.maxScore) * 100) : 0;
                return (
                  <div key={p.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-gray-900 text-xs">{p.name}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{p.score} / {p.maxScore}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          pillarPct >= 80 ? 'bg-emerald-100 text-emerald-700'
                            : pillarPct >= 50 ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {pillarPct}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-testid="security-score-violations">
              <h2>Violations</h2>
              {violations.length === 0 ? (
                <div className="no-results">No violations — all pillars passing.</div>
              ) : (
                <ul className="space-y-2">
                  {violations.map((v) => (
                    <li key={v.id} className="p-3 bg-red-50 rounded-lg border border-red-100 text-xs text-red-800">
                      <span className="font-bold">{v.name}</span> — {v.score} / {v.maxScore}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-testid="security-score-recommendations">
              <h2>Recommendations</h2>
              {recommendations.length === 0 ? (
                <div className="no-results">No recommendations — nothing to remediate.</div>
              ) : (
                <ul className="space-y-2">
                  {recommendations.map((r, i) => (
                    <li key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-100 text-xs text-amber-800">
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
          <span className="hidden" data-testid="security-score-pct">{scoreOutOf100}</span>
        </>
      ) : (
        !error && (
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="no-results">Select a property to view its security score.</div>
          </section>
        )
      )}
    </div>
  );
};

export default SecurityScorePage;
